import { useEffect, useState } from "react";
import {
  doc,
  onSnapshot,
} from "firebase/firestore";

import { db } from "../../firebase";

function normalizeId(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

export default function useFacilityProfile({
  tenantId = "",
  facilityId = "",
  enabled = true,
  debugLabel = "FACILITY PROFILE",
}) {
  const [facilityProfile, setFacilityProfile] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState(null);

  useEffect(() => {
    if (!enabled) {
      setFacilityProfile(null);
      setLoading(false);
      setError(null);

      return undefined;
    }

    if (!tenantId) {
      setFacilityProfile(null);
      setLoading(false);

      setError(
        new Error(
          "Facility profile requires tenantId."
        )
      );

      console.warn(
        `${debugLabel} NOT STARTED — tenantId missing`,
        {
          tenantId,
          facilityId,
        }
      );

      return undefined;
    }

    if (!facilityId) {
      setFacilityProfile(null);
      setLoading(false);

      setError(
        new Error(
          "Facility profile requires facilityId."
        )
      );

      console.warn(
        `${debugLabel} NOT STARTED — facilityId missing`,
        {
          tenantId,
          facilityId,
        }
      );

      return undefined;
    }

    setLoading(true);
    setError(null);

    console.log(
      `${debugLabel} START`,
      {
        tenantId,
        facilityId,
      }
    );

    const facilityRef = doc(
      db,
      "facilityProfiles",
      facilityId
    );

    const unsubscribe = onSnapshot(
      facilityRef,

      (facilityDoc) => {
        if (!facilityDoc.exists()) {
          const missingError = new Error(
            `Facility profile not found: ${facilityId}`
          );

          console.error(
            `${debugLabel} NOT FOUND`,
            {
              tenantId,
              facilityId,
            }
          );

          setFacilityProfile(null);
          setLoading(false);
          setError(missingError);

          return;
        }

        const data = {
          id: facilityDoc.id,
          ...facilityDoc.data(),
        };

        const profileTenantId =
          normalizeId(data?.tenantId);

        /*
         * Protect against accidentally loading
         * a facility profile belonging to a
         * different tenant.
         */
        if (
          profileTenantId &&
          profileTenantId !==
            normalizeId(tenantId)
        ) {
          const scopeError = new Error(
            "Facility profile tenant does not match operational scope."
          );

          console.error(
            `${debugLabel} TENANT MISMATCH`,
            {
              requestedTenantId:
                tenantId,

              requestedFacilityId:
                facilityId,

              profileTenantId,

              profileFacilityId:
                facilityDoc.id,
            }
          );

          setFacilityProfile(null);
          setLoading(false);
          setError(scopeError);

          return;
        }

        console.log(
          `${debugLabel} SUCCESS`,
          {
            tenantId,
            facilityId,
            name:
              data?.facilityName ||
              data?.name ||
              "",
          }
        );

        setFacilityProfile(data);
        setLoading(false);
        setError(null);
      },

      (listenerError) => {
        console.error(
          `${debugLabel} ERROR`,
          {
            tenantId,
            facilityId,

            code:
              listenerError?.code,

            message:
              listenerError?.message,

            error:
              listenerError,
          }
        );

        setFacilityProfile(null);
        setLoading(false);
        setError(listenerError);
      }
    );

    return () => {
      console.log(
        `${debugLabel} STOP`,
        {
          tenantId,
          facilityId,
        }
      );

      unsubscribe();
    };
  }, [
    tenantId,
    facilityId,
    enabled,
    debugLabel,
  ]);

  return {
    facilityProfile,
    loading,
    error,
  };
}